import { useEffect } from 'react'
import { useTranslation } from 'react-i18next'
import { Sun, Moon, ArrowRight, LogOut, Lock } from 'lucide-react'
import { Icon } from '../components/ui/icon'
import { Button } from '../components/ui/button'
import { Empty, EmptyContent, EmptyDescription, EmptyHeader, EmptyMedia, EmptyTitle } from '../components/ui/empty'
import { NetworkSwitcher } from '../components/NetworkSwitcher'
import { useModule } from '../contexts/ModuleContext'
import { useNetwork } from '../contexts/NetworkContext'
import { useTheme } from '../contexts/ThemeContext'
import { useLogout } from '@/hooks/useLogout'
import '../styles/shell.css'

export function ModulePickerPage() {
  const { t } = useTranslation()
  const { modules, setModule } = useModule()
  const { networks, currentNetwork, loading } = useNetwork()
  const { theme, toggleTheme } = useTheme()
  const logout = useLogout()

  // Auto-select when only one module is available (e.g. political-only user)
  useEffect(() => {
    if (!loading && modules.length === 1) {
      setModule(modules[0].id)
    }
  }, [loading, modules, setModule])

  // Modules are not narrowed to the network until it has loaded.
  if (loading || modules.length === 1) return null

  return (
    <div className="picker-shell">
      {/* The network switcher lives here too: a network the user holds no
          module on would otherwise be a dead end. */}
      <div className="absolute top-5 right-5 flex items-center gap-2">
        <NetworkSwitcher />
        <button
          type="button"
          className="picker-theme-toggle"
          onClick={toggleTheme}
          title={theme === 'dark' ? t('login.switchToLight') : t('login.switchToDark')}
        >
          {theme === 'dark' ? <Sun className="size-4" /> : <Moon className="size-4" />}
        </button>
      </div>

      <div className="picker-content">
        <div className="picker-header">
          <img src="/logo.svg" alt="" className="picker-header__logo" />
          <span className="picker-header__brand">ADNUNTIUS</span>
        </div>

        {modules.length === 0 ? (
          <Empty>
            <EmptyHeader>
              <EmptyMedia variant="icon">
                <Lock />
              </EmptyMedia>
              <EmptyTitle>{t('modulePicker.noModulesTitle')}</EmptyTitle>
              <EmptyDescription>
                {t(networks.length > 1 ? 'modulePicker.noModulesBody' : 'modulePicker.noModulesBodySingleNetwork', {
                  network: currentNetwork?.name ?? '—',
                })}
              </EmptyDescription>
            </EmptyHeader>
            <EmptyContent>
              <Button variant="outline" onClick={logout}>
                <LogOut />
                {t('appShell.signOut')}
              </Button>
            </EmptyContent>
          </Empty>
        ) : (
          <>
            <h1 className="picker-title">{t('modulePicker.heading')}</h1>
            <p className="picker-subtitle">{t('modulePicker.subtitle')}</p>

            <div className="picker-grid">
              {modules.map((m) => (
                <button
                  key={m.id}
                  type="button"
                  className="picker-card"
                  onClick={() => setModule(m.id)}
                >
                  <span className="picker-card__icon" style={{ background: m.accent }}>
                    <Icon name={m.icon} className="size-4" />
                  </span>
                  <span className="picker-card__label">{m.label}</span>
                  <span className="picker-card__blurb">{m.blurb}</span>
                  <ArrowRight className="size-4 picker-card__arrow" />
                </button>
              ))}
            </div>
          </>
        )}
      </div>
    </div>
  )
}
